import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { debounceTime, Subject } from 'rxjs';
import { ProformsAdminService } from 'src/app/private/shared/services/proforms/proforms-admin.service';
import { ProFormsQuestionAPIResponse } from 'src/app/private/shared/types/responses/proforms.responses';
import { CompetencyContentUpdate } from '../edit-gi-content-modal/edit-gi-content-modal.component';
import { EditLevelNamesModalComponent } from '../edit-level-names-modal/edit-level-names-modal.component';

export interface RubricFieldTypeSpecs {
  numbLevels: number;
  levelNames: string[];
  competencies: RubricFieldTypeCompetency[];
}
export interface RubricFieldTypeCompetency {
  title: string;
  gis: string[];
  editing?: boolean;
}

@Component({
  selector: 'app-proforms-admin-question-answer-rubric',
  standalone: false,
  templateUrl: './proforms-admin-question-answer-rubric.component.html',
  styleUrl: './proforms-admin-question-answer-rubric.component.scss',
})
export class ProformsAdminQuestionAnswerRubricComponent
  implements OnInit, OnChanges
{
  @Input() question: ProFormsQuestionAPIResponse;

  @ViewChild('editLevelNames')
  editLevelNamesModal: EditLevelNamesModalComponent;

  fieldTypeSpecs: RubricFieldTypeSpecs;

  levelsChange$ = new Subject<number>();

  levelsMax = 5;

  currentTitleBeingEdited: string;

  titleChangeSubject$ = new Subject<{
    competency: RubricFieldTypeCompetency;
    answer: string;
  }>();

  loading = true;

  constructor(
    private proformsAdminService: ProformsAdminService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);

    this.levelsChange$.pipe(debounceTime(300)).subscribe((value) => {
      this.updateNumbLevelsValue(value);
    });

    this.titleChangeSubject$
      .pipe(debounceTime(500))
      .subscribe(({ competency, answer }) => {
        if (answer.trim() === '') return;
        const currentIdx = this.fieldTypeSpecs.competencies.findIndex(
          (competencyItem) => competency.title === competencyItem.title
        );

        this.fieldTypeSpecs.competencies[currentIdx].title = answer;
        this.updateFieldTypeSpecs();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['question'] &&
      changes['question'].currentValue !== changes['question'].previousValue
    ) {
      this.loading = false;
      this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);
    }
  }

  onLevelsValueChange(value: number) {
    this.levelsChange$.next(value);
  }

  updateNumbLevelsValue(newValue: number) {
    const currentLevel = this.fieldTypeSpecs.numbLevels;
    if (newValue > currentLevel) {
      const levelDifference = newValue - currentLevel;
      this.fieldTypeSpecs.numbLevels = newValue;
      this.fieldTypeSpecs.competencies.forEach((competency) => {
        for (let step = 0; step < levelDifference; step += 1) {
          competency.gis.push('');
        }
      });
      for (let step = 0; step < levelDifference; step += 1) {
        this.fieldTypeSpecs.levelNames.push('New Level');
      }
      this.updateFieldTypeSpecs();
    } else if (newValue < currentLevel) {
      const levelDifference = currentLevel - newValue;
      this.fieldTypeSpecs.numbLevels = newValue;
      this.fieldTypeSpecs.competencies.forEach((competency) => {
        competency.gis.splice(-levelDifference);
      });
      this.fieldTypeSpecs.levelNames = this.fieldTypeSpecs.levelNames.splice(
        0,
        this.fieldTypeSpecs.levelNames.length - levelDifference
      );
      this.updateFieldTypeSpecs();
    }
  }

  onTitleChange(competency: RubricFieldTypeCompetency, newtitle: string) {
    this.titleChangeSubject$.next({
      competency,
      answer: newtitle,
    });
  }

  setCurrentTitleBeingEdited(title: string) {
    this.currentTitleBeingEdited = title;
  }

  handleLevelNamesEvent(levelNames: string[]) {
    this.fieldTypeSpecs.levelNames = levelNames;
    this.updateFieldTypeSpecs();
  }

  handleGIsContentEvent(giData: CompetencyContentUpdate) {
    this.fieldTypeSpecs.competencies[giData.index].gis = giData.gis;
    this.updateFieldTypeSpecs();
  }

  // bulk change of data
  updateFieldTypeSpecs() {
    this.loading = true;
    const newValue = JSON.stringify(this.fieldTypeSpecs);
    this.proformsAdminService.updateQuestion(
      this.question.id,
      this.question.proform_form_section_id,
      { field_type_specs: newValue }
    );
  }

  addCompetency() {
    const newCompetency: RubricFieldTypeCompetency = {
      title: 'Competency',
      gis: [],
    };
    for (let step = 0; step < this.fieldTypeSpecs.numbLevels; step += 1) {
      newCompetency.gis.push('');
    }
    this.fieldTypeSpecs.competencies.push(newCompetency);
    this.updateFieldTypeSpecs();
  }

  deleteCompetency(competency: RubricFieldTypeCompetency) {
    this.fieldTypeSpecs.competencies = this.fieldTypeSpecs.competencies.filter(
      (c) => c !== competency
    );
    this.updateFieldTypeSpecs();
  }
}
